import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Gift, Check, X, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const LOGO_URL = "/logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicEventView = () => {
  const { eventId } = useParams();
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceFilter, setPriceFilter] = useState('all');
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [loginOrGuestDialogOpen, setLoginOrGuestDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [customName, setCustomName] = useState('');
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(null);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, itemsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/items`)
      ]);
      setEvent(eventRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/e/${eventId}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openSelectionDialog = (itemId) => {
    setSelectedItemId(itemId);
    
    if (!user) {
      // Show login or guest option
      setLoginOrGuestDialogOpen(true);
    } else {
      // User is logged in, proceed to name dialog
      setCustomName('');
      setSelectionDialogOpen(true);
    }
  };

  const handleGuestSelection = () => {
    setLoginOrGuestDialogOpen(false);
    setSelectionDialogOpen(true);
  };

  const handleSelectItem = async () => {
    if (!selectedItemId) return;
    
    // For guest users, name is required
    if (!user && !customName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/items/${selectedItemId}/select`,
        { custom_name: customName || null },
        { withCredentials: true }
      );
      
      // Store guest name in localStorage for future changes
      if (!user && customName.trim()) {
        localStorage.setItem(`guestName_${eventId}`, customName.trim());
      }
      
      toast.success('Gift selected!');
      setSelectionDialogOpen(false);
      setCustomName('');
      fetchEventData();
    } catch (error) {
      console.error('Error selecting item:', error);
      toast.error(error.response?.data?.detail || 'Failed to select item');
    }
  };

  const handleUnselectItem = async (itemId) => {
    try {
      const guestName = !user ? localStorage.getItem(`guestName_${eventId}`) : null;
      await axios.post(
        `${API}/events/${eventId}/items/${itemId}/unselect`, 
        { guest_name: guestName },
        { withCredentials: true }
      );
      toast.success('Selection removed');
      fetchEventData();
    } catch (error) {
      console.error('Error unselecting item:', error);
      toast.error('Failed to remove selection');
    }
  };

  const handleRequestAddress = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/address`, { withCredentials: true });
      setDeliveryAddress(response.data);
      setAddressDialogOpen(true);
    } catch (error) {
      console.error('Error fetching address:', error);
      toast.error(error.response?.data?.detail || 'Failed to get delivery address');
    }
  };

  // Check if user has selected any gifts
  const userHasSelections = items.some(item => item.selected_by === user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="glass-card p-8 mb-8 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src={LOGO_URL}
              alt="My Wishlist Logo" 
              className="h-20 w-auto"
              data-testid="public-logo"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2" data-testid="public-event-title">{event?.event_name}</h1>
          <p className="text-lg text-gray-600 mb-4" data-testid="public-event-date">{event?.event_date} at {event?.event_time}</p>
          <p className="text-gray-600 mb-4">Choose a gift from the wishlist below!</p>
          
          {!user && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 text-left">
              <p className="text-sm text-gray-700 mb-2">
                <strong>💡 Quick Sign-in Recommended:</strong> Sign in with Google to select gifts and request delivery address. It's fast and helps us improve the experience!
              </p>
              <Button
                onClick={handleLogin}
                className="mt-2"
                data-testid="public-login-button"
              >
                Sign in with Google
              </Button>
            </div>
          )}
          
          {user && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 text-left">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Note:</strong> You can select up to 5 gifts per event. All selections are visible to ensure accountability.
              </p>
              {userHasSelections && (
                <Button
                  onClick={handleRequestAddress}
                  variant="outline"
                  className="gap-2 mt-2"
                  data-testid="request-address-button"
                >
                  <MapPin className="w-4 h-4" />
                  Request Delivery Address
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Wishlist Items */}
        <div className="glass-card p-6">
          {/* Price Filter */}
          {items.length > 0 && (
            <div className="mb-6 flex gap-2 flex-wrap" data-testid="public-price-filter">
              <Button
                onClick={() => setPriceFilter('all')}
                variant={priceFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                data-testid="public-filter-all"
              >
                All Items
              </Button>
              <Button
                onClick={() => setPriceFilter('under500')}
                variant={priceFilter === 'under500' ? 'default' : 'outline'}
                size="sm"
                data-testid="public-filter-under500"
              >
                Under ₹500
              </Button>
              <Button
                onClick={() => setPriceFilter('500-2000')}
                variant={priceFilter === '500-2000' ? 'default' : 'outline'}
                size="sm"
                data-testid="public-filter-500-2000"
              >
                ₹500 - ₹2000
              </Button>
              <Button
                onClick={() => setPriceFilter('2000-5000')}
                variant={priceFilter === '2000-5000' ? 'default' : 'outline'}
                size="sm"
                data-testid="public-filter-2000-5000"
              >
                ₹2000 - ₹5000
              </Button>
              <Button
                onClick={() => setPriceFilter('above5000')}
                variant={priceFilter === 'above5000' ? 'default' : 'outline'}
                size="sm"
                data-testid="public-filter-above5000"
              >
                Above ₹5000
              </Button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center py-12" data-testid="public-no-items">
              <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No items in the wishlist yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="public-items-grid">
              {items.filter(item => {
                if (priceFilter === 'all') return true;
                if (priceFilter === 'under500') return item.price < 500;
                if (priceFilter === '500-2000') return item.price >= 500 && item.price < 2000;
                if (priceFilter === '2000-5000') return item.price >= 2000 && item.price < 5000;
                if (priceFilter === 'above5000') return item.price >= 5000;
                return true;
              }).map((item) => {
                // Check if selected by current user/guest
                let isSelectedByMe = false;
                if (user) {
                  isSelectedByMe = item.selected_by === user.id;
                } else {
                  // For guests, check if selected by this guest's name
                  const guestName = localStorage.getItem(`guestName_${eventId}`);
                  if (guestName && item.selected_by_name) {
                    isSelectedByMe = item.selected_by_name.toLowerCase().trim() === guestName.toLowerCase().trim();
                  }
                }
                const isSelectedByOther = item.selected_by && !isSelectedByMe;

                return (
                  <div
                    key={item.id}
                    className={`glass-card p-4 product-card ${isSelectedByOther ? 'selected-item' : ''} ${item.product_url ? 'cursor-pointer' : ''}`}
                    data-testid={`public-item-card-${item.id}`}
                    onClick={() => {
                      if (item.product_url) {
                        window.open(item.product_url, '_blank');
                      }
                    }}
                  >
                    <img
                      src={item.product_image_url}
                      alt={item.product_name}
                      className="w-full h-48 object-cover rounded-lg mb-3"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                      }}
                      data-testid={`public-item-image-${item.id}`}
                    />
                    <h3 className="font-semibold text-gray-800 mb-1" data-testid={`public-item-name-${item.id}`}>{item.product_name}</h3>
                    <p className="text-xl font-bold text-blue-600 mb-2" data-testid={`public-item-price-${item.id}`}>₹{item.price.toFixed(2)}</p>
                    {item.product_url && (
                      <p className="text-xs text-blue-500 mb-2">Click to view product</p>
                    )}
                    
                    {isSelectedByMe ? (
                      <div className="space-y-2">
                        <p className="text-sm text-green-600 font-medium" data-testid={`public-item-selected-by-me-${item.id}`}>
                          ✓ You selected this gift
                        </p>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnselectItem(item.id);
                          }}
                          variant="outline"
                          className="w-full gap-2"
                          data-testid={`public-unselect-button-${item.id}`}
                        >
                          <X className="w-4 h-4" />
                          Change Selection
                        </Button>
                      </div>
                    ) : isSelectedByOther ? (
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500 font-medium" data-testid={`public-item-selected-by-other-${item.id}`}>
                          ❤️ Reserved by a loved one
                        </p>
                        <p className="text-xs text-gray-400">This gift is already chosen</p>
                      </div>
                    ) : (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openSelectionDialog(item.id);
                        }}
                        className="w-full gap-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
                        data-testid={`public-select-button-${item.id}`}
                      >
                        <Check className="w-4 h-4" />
                        Select This Gift
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Login or Guest Choice Dialog */}
      <Dialog open={loginOrGuestDialogOpen} onOpenChange={setLoginOrGuestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select This Gift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              To select a gift, you can either sign in with your Google account or continue as a guest.
            </p>
            
            <Button
              onClick={handleLogin}
              className="w-full gap-2"
              style={{background: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)'}}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or</span>
              </div>
            </div>

            <Button
              onClick={handleGuestSelection}
              variant="outline"
              className="w-full"
            >
              Continue as Guest
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Selection Dialog */}
      <Dialog open={selectionDialogOpen} onOpenChange={setSelectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select This Gift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {user ? (
              <>
                <p className="text-sm text-gray-600">
                  You're signed in as <strong>{user.name}</strong>. You can optionally enter a different name below.
                </p>
                <div>
                  <Label htmlFor="custom_name">Display Name (Optional)</Label>
                  <Input
                    id="custom_name"
                    placeholder="e.g., On behalf of kids"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    data-testid="custom-name-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to use your Google name
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Please enter your name to select this gift
                </p>
                <div>
                  <Label htmlFor="custom_name">Your Name *</Label>
                  <Input
                    id="custom_name"
                    placeholder="Enter your name"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    data-testid="custom-name-input"
                    required
                  />
                </div>
              </>
            )}
            <Button
              onClick={handleSelectItem}
              className="w-full"
              data-testid="confirm-selection-button"
              disabled={!user && !customName.trim()}
            >
              Confirm Selection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delivery Address</DialogTitle>
          </DialogHeader>
          {deliveryAddress ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Here's the delivery address for <strong>{deliveryAddress.event_name}</strong>:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">Address:</p>
                    <p className="text-gray-700">{deliveryAddress.delivery_address}</p>
                  </div>
                </div>
                {deliveryAddress.delivery_phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-800">Phone:</p>
                      <p className="text-gray-700">{deliveryAddress.delivery_phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-600">Loading address...</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicEventView;